import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { agentAPI } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { formatDate } from '@/lib/utils';
import {
  UserPlus, FileText, CheckCircle, XCircle, Clock, 
  ArrowRight, Briefcase, Shield, Loader2, Activity
} from 'lucide-react';

const typeConfig = {
  agent_created: { icon: UserPlus, color: 'border-emerald-400 bg-emerald-50', dot: 'bg-emerald-500' },
  contract_requested: { icon: FileText, color: 'border-sky-400 bg-sky-50', dot: 'bg-sky-500' },
  status_change: { icon: Clock, color: 'border-amber-400 bg-amber-50', dot: 'bg-amber-500' },
  appointment: { icon: Briefcase, color: 'border-indigo-400 bg-indigo-50', dot: 'bg-indigo-500' },
  transfer: { icon: ArrowRight, color: 'border-violet-400 bg-violet-50', dot: 'bg-violet-500' },
  audit: { icon: Shield, color: 'border-slate-400 bg-slate-50', dot: 'bg-slate-500' },
};

const statusIcons = {
  Approved: <CheckCircle size={12} className="text-emerald-500" />,
  Declined: <XCircle size={12} className="text-red-500" />,
  Terminated: <XCircle size={12} className="text-gray-500" />,
};

export function AgentTimeline({ npn }) {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!npn) return;
    loadTimeline();
  }, [npn]);

  const loadTimeline = async () => {
    try {
      const res = await agentAPI.timeline(npn);
      setTimeline(res.data.timeline || []);
    } catch (err) {
      console.error('Failed to load timeline:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (timeline.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
        <Activity size={32} className="mb-3" />
        <p className="text-sm font-medium">No activity yet</p>
        <p className="text-xs mt-1">Timeline events will appear as the agent progresses</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[500px]" data-testid="agent-timeline">
      <div className="relative pl-6">
        {/* Vertical line */}
        <div className="absolute left-[11px] top-3 bottom-3 w-px bg-slate-200" />

        {timeline.map((event, i) => {
          const config = typeConfig[event.type] || typeConfig.audit;
          const Icon = config.icon;

          return (
            <div
              key={event.event_id || i}
              className="relative flex items-start gap-4 pb-6 last:pb-0"
              data-testid={`timeline-event-${i}`}
            >
              {/* Dot */}
              <div className={`absolute left-0 top-1 w-[22px] h-[22px] rounded-full border-2 flex items-center justify-center bg-white ${config.color} z-10`}>
                {event.status && statusIcons[event.status] ? (
                  statusIcons[event.status]
                ) : (
                  <div className={`w-2 h-2 rounded-full ${config.dot}`} />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 ml-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 leading-tight">
                      {event.title}
                    </p>
                    {event.description && (
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                        {event.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      {event.changed_by && (
                        <span className="text-[10px] text-slate-400">by {event.changed_by}</span>
                      )}
                      {event.performed_by && !event.changed_by && (
                        <span className="text-[10px] text-slate-400">by {event.performed_by}</span>
                      )}
                      <span className="text-[10px] text-slate-400">
                        {event.timestamp ? formatDistanceToNow(new Date(event.timestamp), { addSuffix: true }) : ''}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {event.status && (
                      <Badge variant="outline" className="text-[10px]">
                        {event.status.replace(/_/g, ' ')}
                      </Badge>
                    )}
                    {event.timestamp && (
                      <span className="text-[10px] text-slate-300 whitespace-nowrap">
                        {formatDate(event.timestamp)}
                      </span>
                    )}
                  </div>
                </div>
                {event.link && (
                  <Link to={event.link} className="inline-flex items-center gap-1 mt-1 text-[10px] text-sky-600 hover:text-sky-700">
                    View details <ArrowRight size={10} />
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}
